'use client';

import type { Product } from '@/lib/shopify/types';
import { motion } from 'framer-motion';
import { FeaturedProductCard } from './featured-product-card';

function FeaturedProductItem({
  item,
  index
}: {
  item: Product;
  index: number;
}) {
  // Animation variants for staggered appearance
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.1,
        duration: 0.5,
        ease: [0.25, 0.1, 0.25, 1.0]
      }
    })
  };

  return (
    <motion.div
      custom={index}
      initial="hidden"
      animate="visible"
      variants={itemVariants}
      className="group relative overflow-hidden transition-shadow duration-300"
    >
      <FeaturedProductCard product={item} />
    </motion.div>
  );
}

export function FeaturedProductsGridClient({ products }: { products: Product[] }) {
  return (
    <section className="relative pt-16 pb-10 md:pb-16">
      {/* Background decorative elements */}
      <div className="absolute -left-4 top-0 h-64 w-64 rounded-full bg-[#F1EFEC]/5 blur-3xl" />
      <div className="absolute -right-4 bottom-0 h-64 w-64 rounded-full bg-[#F1EFEC]/5 blur-3xl" />
      <div className="absolute left-1/2 top-1/3 h-32 w-32 -translate-x-1/2 rounded-full bg-[#F1EFEC]/5 blur-2xl" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8 md:mb-12 text-center"
        >
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight">Featured Products</h2>
          <p className="mt-3 md:mt-4 text-base md:text-lg text-muted-foreground">
            Discover our handpicked selection of premium products
          </p>
        </motion.div>

        <div className={`grid gap-4 md:gap-6 mx-auto ${products.length === 1 ? 'grid-cols-1 max-w-md' : products.length === 2 ? 'grid-cols-1 sm:grid-cols-2 max-w-2xl' : products.length === 3 ? 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 max-w-4xl' : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-4'}`}>
          {products.map((item, index) => (
            <FeaturedProductItem
              key={item.id}
              item={item}
              index={index}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
